import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Main as Container } from 'Components/Main';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { getExportFileRopa, getRopaById } from 'services/report-ropa-service';
import { ReportsRopaProcess } from './Process';
import { ReportsRopaArea } from './Area';
import { useReactToPrint } from 'react-to-print';
import { ReportsRopaCompany } from './Company';
import { useForm } from 'react-hook-form';
import { ReportsRopaExecutiveSummary } from './ExecutiveSummary';
import { FILTER } from './filter-types';
import { PageHeader } from 'Components/ui';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BLUE_DEEP = 'var(--vo-cobalto)';

const ExportBtn = styled.button`
  height: 40px;
  background: ${BLUE};
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  position: relative;
  &:hover { background: ${BLUE_DEEP}; }
`;
const ExportDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 16px color-mix(in srgb, var(--vo-escuro) 14%, transparent);
  border: 1px solid var(--vo-cloud);
  min-width: 180px;
  z-index: 200;
  overflow: hidden;
`;
const ExportItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  color: ${INK};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  &:hover { background: var(--vo-mist); }
`;
const ErrorCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 48px 24px;
  text-align: center;
  color: ${MUTED};
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

export const ReportsRopaShow = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { id } = useParams();
  const componentRef = useRef();
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    if (!exportOpen) return;
    const close = () => setExportOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [exportOpen]);

  const { watch } = useForm({
    defaultValues: {
      filter: '',
      area: '',
      process: '',
      data: '',
      category: '',
      processing: '',
      legalBase: '',
      position: '',
    },
  });

  const {
    isLoading,
    error,
    data: ropa,
  } = useQuery('getReportRopaById', async () => {
    const dataRopa = await getRopaById(id);
    if (dataRopa.status === 200) {
      return dataRopa.data;
    }
    return null;
  });

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: '@page { size: 10in 12in }',
  });

  const getContentFiltered = () => {
    const contentRopa = {
      name: ropa.name,
      scope: ropa.scope,
      company: {},
      area: {},
      process: {},
    };
    if (ropa.scope === 'Empresa') {
      contentRopa.company.name = ropa.company.name;
      contentRopa.company.description = ropa.company.description;
      contentRopa.company.areas = [];

      ropa.company.areas.forEach(itemArea => {
        const contentArea = {
          name: itemArea.name,
          description: itemArea.description,
          user: itemArea.user,
          processes: [],
        };
        itemArea.processes.forEach(itemProcess => {
          const contentProcess = {
            name: itemProcess.name,
            descriptionDataFlow: itemProcess.descriptionDataFlow,
            user: itemProcess.user,
            dataMapping: [],
          };
          itemProcess.dataMapping.forEach(itemDataMap => {
            const contentDataMap = {
              name: itemDataMap.name,
              category: itemDataMap.category,
              dataSource: itemDataMap.dataSource,
              holderProfile: itemDataMap.holderProfile,
              retentionPeriod: itemDataMap.retentionPeriod,
              storage: itemDataMap.storage,
              dataProcessing: [],
            };
            itemDataMap.dataProcessing.forEach(itemDataProcess => {
              const contentDataProcess = {
                complementLegalBasis: itemDataProcess.complementLegalBasis,
                destiny: itemDataProcess.destiny,
                finality: itemDataProcess.finality,
                legalBase: itemDataProcess.legalBase,
                observation: itemDataProcess.observation,
                position: itemDataProcess.position,
                processing: itemDataProcess.processing,
                shareType: itemDataProcess.shareType,
                tool: itemDataProcess.tool,
              };
              contentDataMap.dataProcessing.push(contentDataProcess);
            });
            contentProcess.dataMapping.push(contentDataMap);
          });
          contentArea.processes.push(contentProcess);
        });
        contentRopa.company.areas.push(contentArea);
      });

      contentRopa.company.areas = contentRopa.company.areas.filter(areaFilter => {
        if (watch('filter') === FILTER.AREA && !!watch('area')) {
          return areaFilter.name === watch('area');
        }
        if (watch('filter') === FILTER.PROCESS && !!watch('process')) {
          return areaFilter.processes.some(itemProcess => itemProcess.name === watch('process'));
        }
        if (watch('filter') === FILTER.DATA && !!watch('data')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => dataMap.name === watch('data'));
          });
        }
        if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => dataMap.category === watch('category'));
          });
        }
        if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.processing === watch('processing');
              });
            });
          });
        }
        if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.legalBase === watch('legalBase');
              });
            });
          });
        }
        if (watch('filter') === FILTER.POSITION && !!watch('position')) {
          return areaFilter.processes.some(itemProcess => {
            return itemProcess.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.position === watch('position');
              });
            });
          });
        }
        return true;
      });

      contentRopa.company.areas.forEach(areaFilter => {
        areaFilter.processes = areaFilter.processes.filter(processFilter => {
          if (watch('filter') === FILTER.PROCESS && !!watch('process')) {
            return processFilter.name === watch('process');
          }
          if (watch('filter') === FILTER.DATA && !!watch('data')) {
            return processFilter.dataMapping.some(dataMap => dataMap.name === watch('data'));
          }
          if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
            return processFilter.dataMapping.some(dataMap => dataMap.category === watch('category'));
          }
          if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.processing === watch('processing');
              });
            });
          }
          if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.legalBase === watch('legalBase');
              });
            });
          }
          if (watch('filter') === FILTER.POSITION && !!watch('position')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.position === watch('position');
              });
            });
          }
          return true;
        });

        areaFilter.processes.forEach(processFilter => {
          processFilter.dataMapping = processFilter.dataMapping.filter(dataMap => {
            if (watch('filter') === FILTER.DATA && !!watch('data')) {
              return dataMap.name === watch('data');
            }
            if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
              return dataMap.category === watch('category');
            }
            if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
              return dataMap.dataProcessing.some(itemDataProcess => {
                return itemDataProcess.processing === watch('processing');
              });
            }
            if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
              return dataMap.dataProcessing.some(itemDataProcess => {
                return itemDataProcess.legalBase === watch('legalBase');
              });
            }
            if (watch('filter') === FILTER.POSITION && !!watch('position')) {
              return dataMap.dataProcessing.some(itemDataProcess => {
                return itemDataProcess.position === watch('position');
              });
            }
            return true;
          });

          processFilter.dataMapping.forEach(dataMap => {
            dataMap.dataProcessing = dataMap.dataProcessing.filter(dataProcess => {
              if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
                return dataProcess.processing === watch('processing');
              }
              if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
                return dataProcess.legalBase === watch('legalBase');
              }
              if (watch('filter') === FILTER.POSITION && !!watch('position')) {
                return dataProcess.position === watch('position');
              }
              return true;
            });
          });
        });
      });
    } else if (ropa.scope === 'Área') {
      contentRopa.area.name = ropa.area.name;
      contentRopa.area.description = ropa.area.description;
      contentRopa.area.user = ropa.area.user;
      contentRopa.area.processes = [];

      ropa.area.processes.forEach(itemProcess => {
        const contentProcess = {
          name: itemProcess.name,
          descriptionDataFlow: itemProcess.descriptionDataFlow,
          user: itemProcess.user,
          dataMapping: [],
        };
        itemProcess.dataMapping.forEach(itemDataMap => {
          const contentDataMap = {
            name: itemDataMap.name,
            category: itemDataMap.category,
            dataSource: itemDataMap.dataSource,
            holderProfile: itemDataMap.holderProfile,
            retentionPeriod: itemDataMap.retentionPeriod,
            storage: itemDataMap.storage,
            dataProcessing: [],
          };
          itemDataMap.dataProcessing.forEach(itemDataProcess => {
            const contentDataProcess = {
              complementLegalBasis: itemDataProcess.complementLegalBasis,
              destiny: itemDataProcess.destiny,
              finality: itemDataProcess.finality,
              legalBase: itemDataProcess.legalBase,
              observation: itemDataProcess.observation,
              position: itemDataProcess.position,
              processing: itemDataProcess.processing,
              shareType: itemDataProcess.shareType,
              tool: itemDataProcess.tool,
            };
            contentDataMap.dataProcessing.push(contentDataProcess);
          });
          contentProcess.dataMapping.push(contentDataMap);
        });
        contentRopa.area.processes.push(contentProcess);
      });

      contentRopa.area.processes = contentRopa.area.processes
        .filter(processFilter => {
          if (watch('filter') === FILTER.PROCESS && !!watch('process')) {
            return processFilter.name === watch('process');
          }
          if (watch('filter') === FILTER.DATA && !!watch('data')) {
            return processFilter.dataMapping.some(dataMap => dataMap.name === watch('data'));
          }
          if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
            return processFilter.dataMapping.some(dataMap => dataMap.category === watch('category'));
          }
          if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.processing === watch('processing');
              });
            });
          }
          if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.legalBase === watch('legalBase');
              });
            });
          }
          if (watch('filter') === FILTER.POSITION && !!watch('position')) {
            return processFilter.dataMapping.some(dataMap => {
              return dataMap.dataProcessing.some(dataProcess => {
                return dataProcess.position === watch('position');
              });
            });
          }
          return true;
        })
        .map(processFilter => {
          processFilter.dataMapping = processFilter.dataMapping
            .filter(dataMap => {
              if (watch('filter') === FILTER.DATA && !!watch('data')) {
                return dataMap.name === watch('data');
              }
              if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
                return dataMap.category === watch('category');
              }
              if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
                return dataMap.dataProcessing.some(itemDataProcess => {
                  return itemDataProcess.processing === watch('processing');
                });
              }
              if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
                return dataMap.dataProcessing.some(itemDataProcess => {
                  return itemDataProcess.legalBase === watch('legalBase');
                });
              }
              if (watch('filter') === FILTER.POSITION && !!watch('position')) {
                return dataMap.dataProcessing.some(itemDataProcess => {
                  return itemDataProcess.position === watch('position');
                });
              }
              return true;
            })
            .map(dataMap => {
              dataMap.dataProcessing = dataMap.dataProcessing.filter(dataProcess => {
                if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
                  return dataProcess.processing === watch('processing');
                }
                if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
                  return dataProcess.legalBase === watch('legalBase');
                }
                if (watch('filter') === FILTER.POSITION && !!watch('position')) {
                  return dataProcess.position === watch('position');
                }
                return true;
              });
              return dataMap;
            });
          return processFilter;
        });
    } else if (ropa.scope === 'Processo') {
      contentRopa.process.name = ropa.process.name;
      contentRopa.process.descriptionDataFlow = ropa.process.descriptionDataFlow;
      contentRopa.process.user = ropa.process.user;
      contentRopa.process.dataMapping = [];

      ropa.process.dataMapping.forEach(itemDataMap => {
        const contentDataMap = {
          name: itemDataMap.name,
          category: itemDataMap.category,
          dataSource: itemDataMap.dataSource,
          holderProfile: itemDataMap.holderProfile,
          retentionPeriod: itemDataMap.retentionPeriod,
          storage: itemDataMap.storage,
          dataProcessing: [],
        };
        itemDataMap.dataProcessing.forEach(itemDataProcess => {
          const contentDataProcess = {
            complementLegalBasis: itemDataProcess.complementLegalBasis,
            destiny: itemDataProcess.destiny,
            finality: itemDataProcess.finality,
            legalBase: itemDataProcess.legalBase,
            observation: itemDataProcess.observation,
            position: itemDataProcess.position,
            processing: itemDataProcess.processing,
            shareType: itemDataProcess.shareType,
            tool: itemDataProcess.tool,
          };
          contentDataMap.dataProcessing.push(contentDataProcess);
        });
        contentRopa.process.dataMapping.push(contentDataMap);
      });

      contentRopa.process.dataMapping = contentRopa.process.dataMapping
        .filter(dataMap => {
          if (watch('filter') === FILTER.DATA && !!watch('data')) {
            return dataMap.name === watch('data');
          }
          if (watch('filter') === FILTER.CATEGORY && !!watch('category')) {
            return dataMap.category === watch('category');
          }
          if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
            return dataMap.dataProcessing.some(itemDataProcess => {
              return itemDataProcess.processing === watch('processing');
            });
          }
          if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
            return dataMap.dataProcessing.some(itemDataProcess => {
              return itemDataProcess.legalBase === watch('legalBase');
            });
          }
          if (watch('filter') === FILTER.POSITION && !!watch('position')) {
            return dataMap.dataProcessing.some(itemDataProcess => {
              return itemDataProcess.position === watch('position');
            });
          }
          return true;
        })
        .map(dataMap => {
          dataMap.dataProcessing = dataMap.dataProcessing.filter(dataProcess => {
            if (watch('filter') === FILTER.PROCESSING && !!watch('processing')) {
              return dataProcess.processing === watch('processing');
            }
            if (watch('filter') === FILTER.LEGAL_BASE && !!watch('legalBase')) {
              return dataProcess.legalBase === watch('legalBase');
            }
            if (watch('filter') === FILTER.POSITION && !!watch('position')) {
              return dataProcess.position === watch('position');
            }
            return true;
          });
          return dataMap;
        });
    }
    return contentRopa;
  };

  const exportExcel = async () => {
    const response = await getExportFileRopa(getContentFiltered());
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    let fileName = '';
    switch (ropa.scope) {
      case 'Empresa':
        fileName = 'relatorio_ropa_versao_empresa.xlsx';
        break;
      case 'Área':
        fileName = 'relatorio_ropa_versao_area.xlsx';
        break;
      case 'Processo':
        fileName = 'relatorio_ropa_versao_processo.xlsx';
        break;
      default:
        fileName = 'relatorio_ropa_versao_sumario_executivo.xlsx';
    }
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container>
      <PageHeader
        title={ropa?.name ?? ''}
        segments={[
          { label: t('common:breadcrumbs.reports') },
          { label: t('dashboard:ropa.breadcrumbLabel'), route: '/reports/ropa' },
          { label: ropa?.name ?? '' },
        ]}
        actions={
          <ExportBtn
            onClick={e => { e.stopPropagation(); setExportOpen(o => !o); }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {t('dashboard:ropa.show.export')}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
            {exportOpen && (
              <ExportDropdown onClick={e => e.stopPropagation()}>
                <ExportItem onClick={() => { handlePrint(); setExportOpen(false); }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  {t('dashboard:ropa.show.exportPdf')}
                </ExportItem>
                <ExportItem onClick={() => { exportExcel(); setExportOpen(false); }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <path d="M3 9h18M3 15h18M9 3v18"/>
                  </svg>
                  {t('dashboard:ropa.show.exportExcel')}
                </ExportItem>
              </ExportDropdown>
            )}
          </ExportBtn>
        }
      />

      {isLoading && <PageLoaderShow />}
      {error && <ErrorCard>{t('dashboard:ropa.show.error')}</ErrorCard>}
      {!isLoading && !error && (
        <div ref={componentRef}>
          {ropa.scope === 'Empresa' && (
            <ReportsRopaCompany
              company={ropa.company}
              ropaName={ropa.name}
              ropaScope={ropa.scope}
              ropaDate={ropa.date}
              filter={watch('filter')}
              area={watch('area')}
              process={watch('process')}
              data={watch('data')}
              category={watch('data')}
              processing={watch('processing')}
              legalBase={watch('legalBase')}
              position={watch('position')}
            />
          )}
          {ropa.scope === 'Área' && (
            <ReportsRopaArea
              area={ropa.area}
              isPageView={true}
              ropaName={ropa.name}
              ropaScope={ropa.scope}
              ropaDate={ropa.date}
              filter={watch('filter')}
              process={watch('process')}
              data={watch('data')}
              category={watch('data')}
              legalBase={watch('legalBase')}
              processing={watch('processing')}
              position={watch('position')}
            />
          )}
          {ropa.scope === 'Processo' && (
            <ReportsRopaProcess
              process={ropa.process}
              isPageView={true}
              ropaName={ropa.name}
              ropaScope={ropa.scope}
              ropaDate={ropa.date}
              ropaArea={ropa.process?.areas?.[0]?.name}
              filter={watch('filter')}
              data={watch('data')}
              category={watch('category')}
              legalBase={watch('legalBase')}
              processing={watch('processing')}
              position={watch('position')}
            />
          )}
          {ropa.scope === 'Sumário Executivo' && <ReportsRopaExecutiveSummary dados={ropa.executiveSummary} />}
        </div>
      )}
    </Container>
  );
};
